import { useContext, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Tag } from "primereact/tag";

import TopicUpdate from "./TopicUpdate";
import { ITopic, ITopicListProps } from "./Topic.model";
import { TopicService } from "../../service/Topic/topic.service";
import { Can } from "../../ability/can";
import {
  PERMISSIONS_ACTION,
  PERMISSIONS_MODEL,
} from "../../library/utilities/constants/permissions";
import { MessageContext } from "../../context/MessageContext";

const TopicList = ({ openNew, loadTopics, data, loading }: ITopicListProps) => {
  const [topicDialog, setTopicDialog] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState<ITopic | null>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const { pushMessageForToast } = useContext(MessageContext);

  const deleteTopic = (id: number) => {
    setDeleteId(id);
    setConfirmDialog(true);
  };

  const confirmDeleteTopic = async () => {
    if (!deleteId) return;
    const response = await TopicService.remove(deleteId);

    setConfirmDialog(false);

    if (response.success) {
      pushMessageForToast({
        severity: "success",
        message: "Topic deleted successfully",
      });
      loadTopics();
    } else {
      const errorMessage =
        response.errors?.error ||
        response.errors?.detail ||
        "Failed to delete topic";
      pushMessageForToast({
        severity: "error",
        message: errorMessage,
      });
    }
  };

  const actionBodyTemplate = (row: ITopic) => {
    return (
      <div className="flex gap-2">
        <Can I={PERMISSIONS_ACTION.TOPIC_CHANGE} a={PERMISSIONS_MODEL.TOPIC}>
          <Button
            label="Edit"
            icon="pi pi-pencil"
            onClick={() => {
              setSelectedTopic(row);
              setTopicDialog(true);
            }}
          />
        </Can>

        <Can I={PERMISSIONS_ACTION.TOPIC_DELETE} a={PERMISSIONS_MODEL.TOPIC}>
          <Button
            label="Delete"
            icon="pi pi-trash"
            severity="danger"
            onClick={() => deleteTopic(row.id)}
          />
        </Can>
      </div>
    );
  };

  const statusBodyTemplate = (row: ITopic) => {
    return row.is_active ? (
      <Tag value="Active" severity="success" />
    ) : (
      <Tag value="Inactive" severity="danger" />
    );
  };

  const topicColumns = [
    {
      field: "name",
      header: "Topic Name",
    },
    {
      field: "description",
      header: "Description",
    },
    {
      field: "is_active",
      header: "Status",
      body: statusBodyTemplate,
    },
  ];

  const [globalFilter, setGlobalFilter] = useState("");
  const filteredData = data.filter((topic: ITopic) => {
    const search = globalFilter.toLowerCase();
    return (
      topic.name.toLowerCase().includes(search) ||
      (topic.description && topic.description.toLowerCase().includes(search))
    );
  });

  return (
    <>
      <Dialog
        visible={topicDialog}
        header="Update Topic"
        modal
        onHide={() => setTopicDialog(false)}
        className="w-5">
        {selectedTopic && (
          <TopicUpdate
            data={selectedTopic}
            setTopicDialog={setTopicDialog}
            loadTopics={loadTopics}
          />
        )}
      </Dialog>

      {/* Topic Table */}
      <DataTable
        value={filteredData}
        loading={loading}
        dataKey="id"
        paginator
        rows={10}
        header={
          <div className="flex justify-content-between align-items-center gap-3">
            <h3>Topic Management</h3>
            <div className="flex">
              <span className="p-input-icon-left mr-3">
                <i className="pi pi-search" />
                <input
                  type="text"
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  placeholder="Search by name, description"
                  className="p-inputtext p-component"
                />
              </span>
              <Can I={PERMISSIONS_ACTION.TOPIC_ADD} a={PERMISSIONS_MODEL.TOPIC}>
                <Button label="New Topic" onClick={openNew} />
              </Can>
            </div>
          </div>
        }>
        {topicColumns.map((col) => (
          <Column
            sortable
            key={col.field}
            field={col.field}
            header={col.header}
            body={col.body}
          />
        ))}

        <Column header="Actions" body={actionBodyTemplate} />
      </DataTable>

      {/* Delete Confirmation */}
      <ConfirmDialog
        visible={confirmDialog}
        onHide={() => setConfirmDialog(false)}
        message="Are you sure you want to delete this topic?"
        header="Delete Confirmation"
        icon="pi pi-exclamation-triangle"
        accept={confirmDeleteTopic}
        reject={() => setConfirmDialog(false)}
      />
    </>
  );
};

export default TopicList;
